import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Check, KeyRound, Mail, UserCog, Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const STAGES = ["password", "mfa", "email_otp", "user_manager"];

const Stepper = ({ stage }) => {
  const { t } = useI18n();
  const idx = STAGES.indexOf(stage);
  return (
    <ol className="mb-8 grid grid-cols-5 gap-2" data-testid="auth-stepper">
      {["step1", "step2", "step3", "step4", "step5"].map((k, i) => (
        <li key={k} data-testid={`auth-step-${i + 1}`} className="flex flex-col items-center gap-2 text-center">
          <span className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${i < idx ? "bg-emerald-600 text-white" : i === idx ? "bg-sky-600 text-white" : "bg-slate-200 text-slate-500"}`}>
            {i < idx ? <Check className="h-4 w-4" /> : i + 1}
          </span>
          <span className={`text-[10px] font-semibold leading-tight sm:text-[11px] ${i === idx ? "text-slate-900" : "text-slate-500"}`}>{t(k)}</span>
        </li>
      ))}
    </ol>
  );
};

const Otp = ({ value, onChange, testId }) => (
  <div dir="ltr" className="flex justify-center">
    <InputOTP maxLength={6} value={value} onChange={onChange} data-testid={testId} inputMode="numeric" autoFocus>
      <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
    </InputOTP>
  </div>
);

export default function Login() {
  const { t, lang, setLang } = useI18n();
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [stage, setStage] = useState("password");
  const [chain, setChain] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });
  const [code, setCode] = useState("");
  const [mfaInfo, setMfaInfo] = useState(null);
  const [otpInfo, setOtpInfo] = useState(null);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const call = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const doLogin = (e) => { e.preventDefault(); call(async () => {
    const { data } = await api.post("/auth/login", form);
    setChain(data.chain_token); setMfaInfo(data); setCode(""); setStage("mfa");
  }); };
  const doMfa = (e) => { e.preventDefault(); call(async () => {
    const { data } = await api.post("/auth/mfa/verify", { chain_token: chain, code });
    setChain(data.chain_token); setOtpInfo(data); setCode(""); setStage("email_otp");
  }); };
  const doOtp = (e) => { e.preventDefault(); call(async () => {
    const { data } = await api.post("/auth/email-otp/verify", { chain_token: chain, code });
    setChain(data.chain_token); setProfile(data.profile); setStage("user_manager");
  }); };
  const resend = () => call(async () => {
    const { data } = await api.post("/auth/email-otp/resend", { chain_token: chain });
    setOtpInfo((o) => ({ ...o, ...data })); toast.success(t("resend"));
  });
  const enter = () => call(async () => {
    const { data } = await api.post("/auth/portal/enter", { chain_token: chain });
    setSession(data.access_token, data.user); nav("/dashboard");
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-4" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, rgba(2,132,199,0.25), transparent 40%)" }}>
      <div className="w-full max-w-lg">
        <div className="mb-6 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-600"><Shield className="h-5 w-5" /></div>
            <div><p className="font-bold">{t("appName")}</p><p className="text-xs text-slate-400">{t("appTag")}</p></div>
          </div>
          <Button data-testid="login-language-toggle" variant="ghost" className="text-white hover:bg-white/10 hover:text-white" onClick={() => setLang(lang === "en" ? "ar" : "en")}>
            <Languages className="h-4 w-4" /><span className="ms-1 text-xs">{lang === "en" ? "عربي" : "EN"}</span>
          </Button>
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-xl sm:p-8">
          <Stepper stage={stage} />
          {error && <div data-testid="auth-error" role="alert" className="mb-4 rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">{error}</div>}

          {stage === "password" && (
            <form onSubmit={doLogin} className="space-y-4" data-testid="login-form">
              <div><h1 className="text-2xl font-bold text-slate-900">{t("signInTitle")}</h1><p className="text-sm text-slate-500">{t("signInSub")}</p></div>
              <div className="space-y-2"><Label htmlFor="email">{t("email")}</Label>
                <Input id="email" data-testid="login-email-input" type="email" dir="ltr" autoComplete="username" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl" /></div>
              <div className="space-y-2"><Label htmlFor="password">{t("password")}</Label>
                <Input id="password" data-testid="login-password-input" type="password" dir="ltr" autoComplete="current-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl" /></div>
              <Button data-testid="login-submit-button" disabled={busy} className="h-11 w-full rounded-xl bg-slate-900 hover:bg-slate-800">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("continue")}</Button>
            </form>
          )}

          {stage === "mfa" && (
            <form onSubmit={doMfa} className="space-y-5" data-testid="mfa-form">
              <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-sky-600" /><h2 className="text-xl font-semibold text-slate-900">{t("mfaTitle")}</h2></div>
              <p className="text-sm text-slate-600">{mfaInfo?.qr ? t("mfaEnroll") : t("mfaEnter")}</p>
              {mfaInfo?.qr && (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 p-4" data-testid="mfa-enrollment">
                  <img src={mfaInfo.qr} alt="MFA QR" className="h-44 w-44" data-testid="mfa-qr-image" />
                  <p className="text-xs text-slate-500">{t("secretKey")}: <code dir="ltr" data-testid="mfa-secret" className="font-mono text-slate-800">{mfaInfo.secret}</code></p>
                </div>
              )}
              <Otp value={code} onChange={setCode} testId="mfa-code-input" />
              <Button data-testid="mfa-submit-button" disabled={busy || code.length !== 6} className="h-11 w-full rounded-xl bg-slate-900 hover:bg-slate-800">{t("verify")}</Button>
            </form>
          )}

          {stage === "email_otp" && (
            <form onSubmit={doOtp} className="space-y-5" data-testid="email-otp-form">
              <div className="flex items-center gap-2"><Mail className="h-5 w-5 text-sky-600" /><h2 className="text-xl font-semibold text-slate-900">{t("otpTitle")}</h2></div>
              <p className="text-sm text-slate-600" data-testid="otp-delivery-status">
                {otpInfo?.delivery === "sent" ? <>{t("otpSent")} <bdi className="font-semibold">{otpInfo.email}</bdi></> : t("otpFailed")}
              </p>
              <Otp value={code} onChange={setCode} testId="email-otp-code-input" />
              <Button data-testid="email-otp-submit-button" disabled={busy || code.length !== 6} className="h-11 w-full rounded-xl bg-slate-900 hover:bg-slate-800">{t("verify")}</Button>
              <button type="button" data-testid="email-otp-resend-button" onClick={resend} disabled={busy} className="w-full text-center text-sm font-medium text-sky-700 hover:underline">{t("resend")}</button>
            </form>
          )}

          {stage === "user_manager" && profile && (
            <div className="space-y-5" data-testid="user-manager-gate">
              <div className="flex items-center gap-2"><UserCog className="h-5 w-5 text-sky-600" /><h2 className="text-xl font-semibold text-slate-900">{t("umTitle")}</h2></div>
              <p className="text-sm text-slate-600">{t("umSub")}</p>
              <dl className="grid grid-cols-3 gap-3 rounded-xl bg-slate-50 p-4 text-sm">
                <dt className="text-slate-500">{t("name")}</dt><dd className="col-span-2 font-medium" data-testid="gate-profile-name">{profile.name}</dd>
                <dt className="text-slate-500">{t("role")}</dt><dd className="col-span-2 font-mono text-xs" data-testid="gate-profile-role">{profile.role}{profile.is_default_admin && ` · ${t("defaultAdmin")}`}</dd>
                <dt className="text-slate-500">{t("groups")}</dt><dd className="col-span-2">{profile.groups.map((g) => g.name).join(", ") || "—"}</dd>
                <dt className="text-slate-500">{t("status")}</dt><dd className="col-span-2 inline-flex items-center gap-1 text-emerald-700"><Check className="h-4 w-4" />{t("active")}</dd>
              </dl>
              <Button data-testid="enter-portal-button" onClick={enter} disabled={busy} className="h-11 w-full rounded-xl bg-sky-600 hover:bg-sky-700">{t("enterPortal")}</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
